/**
 * Meta tags API utilities
 * Fetch and manage meta tags from Sanity CMS
 */

import { client } from '../configs/sanity';
import imageUrlBuilder from '@sanity/image-url';

const builder = imageUrlBuilder(client);

/**
 * Get meta tags for a specific page from Sanity
 * @param {string} pageSlug - The page slug (e.g., "home", "about", "services")
 * @returns {Promise<Object>} Meta tags configuration with defaults
 */
export const getMetaTagsFromSanity = async (pageSlug) => {
  try {
    const query = `*[_type == "siteMetaTags" && pageSlug == $slug && isActive == true][0]`;
    const data = await client.fetch(query, { slug: pageSlug });

    // If no custom config found, try to get global defaults
    if (!data) {
      const globalQuery = `*[_type == "siteMetaTags" && pageSlug == "global" && isActive == true][0]`;
      const globalData = await client.fetch(globalQuery);

      if (globalData) {
        return formatMetaTags(globalData);
      }

      // Return built-in defaults if nothing found in Sanity
      return getDefaultMetaTags(pageSlug);
    }

    return formatMetaTags(data);
  } catch (error) {
    return getDefaultMetaTags(pageSlug);
  }
};

/**
 * Get all meta tags pages configured in Sanity
 * @returns {Promise<Array>} Array of all meta tags configurations
 */
export const getAllMetaTags = async () => {
  try {
    const query = `*[_type == "siteMetaTags" && isActive == true] | order(pageSlug)`;
    const data = await client.fetch(query);
    return data.map(formatMetaTags);
  } catch (error) {
    return [];
  }
};

/**
 * Format meta tags data from Sanity response
 * @param {Object} data - Raw data from Sanity
 * @returns {Object} Formatted meta tags
 */
const formatMetaTags = (data) => {
  return {
    pageSlug: data.pageSlug,
    pageTitle: data.pageTitle,
    metaDescription: data.metaDescription,
    keywords: data.keywords || [],
    ogTitle: data.ogTitle || data.pageTitle,
    ogDescription: data.ogDescription || data.metaDescription,
    ogImage: data.ogImage ? builder.image(data.ogImage).url() : undefined,
    twitterTitle: data.twitterTitle || data.ogTitle || data.pageTitle,
    twitterDescription:
      data.twitterDescription || data.ogDescription || data.metaDescription,
    twitterImage: data.twitterImage
      ? builder.image(data.twitterImage).url()
      : data.ogImage
      ? builder.image(data.ogImage).url()
      : undefined,
    pageUrl: data.pageUrl,
    ogType: data.ogType || 'website',
  };
};

/**
 * Default meta tags with fallback values
 * @param {string} pageSlug - Page slug
 * @returns {Object} Default meta tags for page
 */
const getDefaultMetaTags = (pageSlug) => {
  const defaults = {
    global: {
      pageSlug: 'global',
      pageTitle: 'build:base | Modern Architecture & Design',
      metaDescription:
        'Discover innovative architecture and design solutions with build:base. Premium construction and design services.',
      keywords: [
        'architecture',
        'design',
        'construction',
        'modern',
        'building',
      ],
      pageUrl: '/',
      ogType: 'website',
    },
    home: {
      pageSlug: 'home',
      pageTitle: 'build:base | Modern Architecture & Design',
      metaDescription:
        'Discover innovative architecture and design solutions with build:base. Premium construction and design services.',
      keywords: [
        'architecture',
        'design',
        'construction',
        'modern',
        'building',
      ],
      pageUrl: '/',
      ogType: 'website',
    },
    about: {
      pageSlug: 'about',
      pageTitle: 'About Us | build:base',
      metaDescription:
        'Learn about our team, vision, and the values that drive our innovative approach to architecture and design.',
      keywords: ['about', 'team', 'architecture', 'design', 'company'],
      pageUrl: '/about',
      ogType: 'website',
    },
    services: {
      pageSlug: 'services',
      pageTitle: 'Our Services | build:base',
      metaDescription:
        'Comprehensive architecture, interior design, and construction management services tailored to your needs.',
      keywords: [
        'services',
        'architecture',
        'design',
        'construction',
        'consulting',
      ],
      pageUrl: '/services',
      ogType: 'website',
    },
    contact: {
      pageSlug: 'contact',
      pageTitle: 'Contact Us | build:base',
      metaDescription:
        "Get in touch with our team. We're ready to discuss your next project and bring your vision to life.",
      keywords: [
        'contact',
        'inquire',
        'get in touch',
        'consultation',
        'project',
      ],
      pageUrl: '/contact',
      ogType: 'website',
    },
  };

  return defaults[pageSlug] || defaults.global;
};

/**
 * Format meta tags for React component use
 * @param {Object} metaTags - Meta tags configuration
 * @returns {Object} Formatted for useMetaTags
 */
export const formatForUseMetaTags = (metaTags) => {
  return {
    title: metaTags.pageTitle,
    description: metaTags.metaDescription,
    image: metaTags.ogImage,
    url: metaTags.pageUrl,
    type: metaTags.ogType,
  };
};
